const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

function getAuthHeader(): Record<string, string> {
  const token = sessionStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function clearAuthSession() {
  sessionStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
}

function handleUnauthorized() {
  clearAuthSession();
  window.location.href = "/auth/sign-in";
}

async function parseErrorResponse(res: Response): Promise<never> {
  const bodyText = await res.text().catch(() => "");
  if (bodyText) {
    try {
      const parsed = JSON.parse(bodyText) as {
        detail?: string | Array<{ msg?: string }>;
        message?: string;
      };
      if (typeof parsed.detail === "string" && parsed.detail.trim()) {
        throw new Error(parsed.detail);
      }
      if (Array.isArray(parsed.detail) && parsed.detail.length > 0) {
        const firstMsg = parsed.detail[0]?.msg;
        if (firstMsg) throw new Error(firstMsg);
      }
      if (parsed.message) {
        throw new Error(parsed.message);
      }
    } catch (error) {
      if (!(error instanceof SyntaxError)) {
        throw error;
      }
    }
  }
  throw new Error(`API error: ${res.status}`);
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: BodyInit | null;
  isMultipart?: boolean;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { headers, isMultipart = false, body, ...rest } = options;
  const defaultHeaders = isMultipart ? {} : { "Content-Type": "application/json" };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    body,
    headers: {
      ...defaultHeaders,
      ...getAuthHeader(),
      ...((headers as Record<string, string>) ?? {}),
    },
  });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    return parseErrorResponse(res);
  }

  if (res.status === 204) return undefined as T;

  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body == null ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body == null ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body == null ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData): Promise<T> =>
    request<T>(path, {
      method: "POST",
      body: formData,
      isMultipart: true,
    }),
};
